import { FlyoutListMenuWidget, MenuButtonWidget } from '@tableau/widgets';
import React from 'react';
import { match, useHistory } from 'react-router-dom';
import * as TypeStyle from 'typestyle';

import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { Icons } from '~src/commonInfra/components/Icons';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';

import { IExtension, IPackage } from '../models/IPackage';
import { PACKAGES_EXTENSION_ROW_ACTIONS_TEST_ID } from '../PackagesTestIds';
import { GetPackageService } from '../serverApi/requests/GetPackage';

export type PackageDetailsSubspaceProps = { luid: string };
export type PackageDetailsSubspaceRoutedProps = match<PackageDetailsSubspaceProps> & {
  // Defaults to the admin settings/packages list. The per-user tab passes its
  // own back href.
  backHref?: string;
  // When false (e.g. on the per-user packages tab), admin-only actions like the
  // per-extension Configure menu are hidden. Defaults to true.
  showAdminActions?: boolean;
};

export const PACKAGE_DETAILS_SUBSPACE_TEST_ID = 'package-details-subspace';
export const PACKAGE_DETAILS_BACK_LINK_TEST_ID = 'package-details-back';
export const PACKAGE_DETAILS_EXTENSIONS_EMPTY_TEST_ID = 'package-details-extensions-empty';

// Detail page for a single Tableau Package: metadata header plus the list of
// declared extensions. Reached via packageDetailsPath(luid).
export function PackageDetailsSubspace(props: PackageDetailsSubspaceRoutedProps): React.ReactElement {
  const { params, backHref, showAdminActions } = props;
  const [pkg, setPkg] = React.useState<IPackage | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setPkg(null);
    setLoadError(null);
    GetPackageService.getPackage(params.luid)
      .then(response => {
        setPkg(response.package ?? null);
      })
      .catch((err: { data?: { error?: { detail?: string } } }) => {
        setLoadError(err?.data?.error?.detail ?? Messages.packages_details_loadError());
      });
  }, [params.luid]);

  return (
    <div data-tb-test-id={PACKAGE_DETAILS_SUBSPACE_TEST_ID} style={{ padding: 16 }}>
      <PackageDetailsBackLink overrideHref={backHref} />
      {loadError !== null && (
        <div className="tb-warning-text" style={{ marginTop: 12 }}>{loadError}</div>
      )}
      {loadError === null && pkg === null && (
        <div style={{ marginTop: 12 }}>{'Loading...'}</div>
      )}
      {pkg !== null && renderPackageContents(pkg, showAdminActions !== false)}
    </div>
  );
}

function renderPackageContents(pkg: IPackage, showAdminActions: boolean): React.ReactNode {
  return (
    <>
      <SettingsSection title={pkg.name ?? ''} description={pkg.author ?? ''}>
        <dl style={{ margin: 0 }}>
          <DetailField label={'Package ID'} value={pkg.manifestId} />
          <DetailField label={'Version'} value={pkg.version} />
          <DetailField
            label={Messages.packages_columnHeader_enabled()}
            value={pkg.enabled ? Messages.packages_state_on() : Messages.packages_state_off()} />
          <DetailField
            label={Messages.packages_columnHeader_defaultActivation()}
            value={pkg.defaultActivation ? Messages.packages_state_on() : Messages.packages_state_off()} />
          <DetailField label={'Uploaded'} value={pkg.createdAt} />
          <DetailField label={'LUID'} value={pkg.luid} />
        </dl>
      </SettingsSection>
      <SettingsSection title={Messages.packages_details_extensionsHeader()}>
        {renderExtensionsTable(pkg.extensions?.extension ?? [], showAdminActions)}
      </SettingsSection>
    </>
  );
}

function renderExtensionsTable(extensions: IExtension[], showAdminActions: boolean): React.ReactNode {
  if (extensions.length === 0) {
    return (
      <div data-tb-test-id={PACKAGE_DETAILS_EXTENSIONS_EMPTY_TEST_ID}>
        {Messages.packages_details_extensionsEmpty()}
      </div>
    );
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={cellStyle}>{'Extension ID'}</th>
          {showAdminActions && <th style={cellStyle}>{'Actions'}</th>}
          <th style={cellStyle}>{'Type'}</th>
          <th style={cellStyle}>{'LUID'}</th>
        </tr>
      </thead>
      <tbody>
        {extensions.map(ext => (
          <tr key={ext.luid}>
            <td style={cellStyle}>{ext.manifestId}</td>
            {showAdminActions && <td style={cellStyle}><ExtensionRowActions ext={ext} /></td>}
            <td style={cellStyle}>{ext.extensionType}</td>
            <td style={cellStyle}>{ext.luid}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Uses history.goBack so the URL prefix (/server vs /site/<name>) is
// preserved on single-site servers. Mirrors ConnectedApplicationBackButton.
function PackageDetailsBackLink(props: { overrideHref?: string }): JSX.Element {
  const history = useHistory();
  const siteUrls = siteUrlsSelector(getAppStore().getState());
  const href = props.overrideHref ?? (siteUrls ? siteUrls.settingsPackagesUrl() : '#');
  return (
    <a
      href={href}
      onClick={(e) => {
        if (props.overrideHref === undefined) {
          e.preventDefault();
          history.goBack();
        }
      }}
      data-tb-test-id={PACKAGE_DETAILS_BACK_LINK_TEST_ID}>
      {'← '}{Messages.packages_details_backText()}
    </a>
  );
}

function ExtensionRowActions(props: { ext: IExtension }): JSX.Element {
  const { ext } = props;
  const buttonRef = React.useRef<HTMLButtonElement>();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const close = React.useCallback(() => setIsOpen(false), []);
  const toggle = React.useCallback(() => setIsOpen(o => !o), []);

  const handleSelect = React.useCallback((value: string) => {
    setIsOpen(false);
    if (value === 'configure') {
      // TODO: open per-extension configuration UI.
    }
  }, []);

  const menuItemInfos = [
    { value: 'configure', displayValue: Messages.packages_extensionActions_configure() },
  ];

  const flyoutId = `packages-extension-row-actions-flyout-${ext.luid ?? ''}`;
  const testId = `${PACKAGES_EXTENSION_ROW_ACTIONS_TEST_ID}-${ext.luid ?? ''}`;

  return (
    <span className={inlineBlockClass}>
      <MenuButtonWidget
        aria-controls={flyoutId}
        aria-label={Messages.moreActions()}
        className={moreActionsButtonStyleName}
        onClick={toggle}
        ref={buttonRef}
        testId={testId}>
        <Icons.actionIcon />
      </MenuButtonWidget>
      {isOpen && (
        <FlyoutListMenuWidget
          allowAutoPosition
          anchorEl={buttonRef.current}
          id={flyoutId}
          menuItemInfos={menuItemInfos}
          menuStyle={{ minWidth: 144 }}
          onSelect={handleSelect}
          requestCloseAll={close}
          testId={`${testId}-flyout`}
          toggleEl={buttonRef.current} />
      )}
    </span>
  );
}

function DetailField(props: { label: string, value: string | undefined }): React.ReactElement {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
      <dt style={{ minWidth: 120, color: '#5a5a5a' }}>{props.label}</dt>
      <dd style={{ margin: 0, fontWeight: 600 }}>{props.value ?? '—'}</dd>
    </div>
  );
}

const cellStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderBottom: '1px solid #ddd',
  textAlign: 'left',
};

const inlineBlockClass = TypeStyle.style({
  display: 'inline-block',
});

const moreActionsButtonStyleName = TypeStyle.style({
  border: 'none',
  verticalAlign: 'middle',
  $nest: {
    '&&': { height: 20, width: 20, minHeight: 20, minWidth: 20, padding: 0 },
    '&&::after': { display: 'none' },
  },
});
